import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarUI } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { calendarApi } from '../lib/api';
import {
  Calendar as CalendarIcon, Plus, Clock, MapPin, Eye, EyeOff,
  Loader2, ChevronLeft, ChevronRight, Briefcase, Lock, Globe
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, isToday } from 'date-fns';

const Calendar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    start_time: '',
    end_time: '',
    event_type: 'personal',
    is_public: false,
    description: '',
    location: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCalendarData();
  }, [currentMonth, user]);

  const loadCalendarData = async () => {
    try {
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      const response = await calendarApi.getEvents({
        user_id: user.user_id,
        start_date: startDate,
        end_date: endDate
      });
      
      setEvents(response.data.events || []);
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Failed to load calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async () => {
    if (!eventForm.title || !eventForm.start_time || !eventForm.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await calendarApi.createEvent({
        ...eventForm,
        start_time: `${format(selectedDate, 'yyyy-MM-dd')}T${eventForm.start_time}:00`,
        end_time: `${format(selectedDate, 'yyyy-MM-dd')}T${eventForm.end_time}:00`
      });
      toast.success('Event added to your calendar');
      setShowAddEvent(false);
      setEventForm({
        title: '',
        start_time: '',
        end_time: '',
        event_type: 'personal',
        is_public: false,
        description: '',
        location: ''
      });
      loadCalendarData();
    } catch (error) {
      toast.error('Failed to add event');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEventVisibility = async (eventId, currentVisibility) => {
    try {
      await calendarApi.updateVisibility(eventId, !currentVisibility);
      toast.success(`Event is now ${!currentVisibility ? 'public' : 'private'}`);
      loadCalendarData();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayEvents = events.filter(e => e.start_time?.startsWith(dateStr));
    const dayBookings = bookings.filter(b => b.start_time?.startsWith(dateStr));
    return { events: dayEvents, bookings: dayBookings };
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'booked': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'available': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'unavailable': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'personal': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Pad start of month to align with day of week
  const startDay = startOfMonth(currentMonth).getDay();
  const paddingDays = Array(startDay).fill(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="calendar-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              My Calendar
            </h1>
            <p className="text-muted-foreground">
              Manage your bookings and availability
            </p>
          </div>
          
          <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
            <DialogTrigger asChild>
              <Button className="rounded-full btn-glow" data-testid="add-event-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
                  Add Calendar Event
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Select Date</Label>
                  <CalendarUI
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="rounded-xl border mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    placeholder="e.g., Studio Session, Private Event"
                    className="mt-1"
                    data-testid="event-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_time">Start Time *</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={eventForm.start_time}
                      onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                      className="mt-1"
                      data-testid="event-start-time"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_time">End Time *</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={eventForm.end_time}
                      onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                      className="mt-1"
                      data-testid="event-end-time"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="event_type">Event Type</Label>
                  <Select
                    value={eventForm.event_type}
                    onValueChange={(val) => setEventForm({ ...eventForm, event_type: val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="event-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="available">Available for Booking</SelectItem>
                      <SelectItem value="unavailable">Unavailable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="location">Location (Optional)</Label>
                  <Input
                    id="location"
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    placeholder="Event location"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Additional details..."
                    className="mt-1"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                  <div className="flex items-center gap-2">
                    {eventForm.is_public ? (
                      <Globe className="h-4 w-4 text-green-400" />
                    ) : (
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium text-sm">Public Event</p>
                      <p className="text-xs text-muted-foreground">
                        {eventForm.is_public 
                          ? 'Visible on your public profile' 
                          : 'Only visible to you'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={eventForm.is_public}
                    onCheckedChange={(checked) => setEventForm({ ...eventForm, is_public: checked })}
                    data-testid="event-public-toggle"
                  />
                </div>

                <Button
                  className="w-full rounded-xl"
                  onClick={handleAddEvent}
                  disabled={submitting || !selectedDate}
                  data-testid="save-event-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Event
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    data-testid="prev-month-btn"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {format(currentMonth, 'MMMM yyyy')}
                  </h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    data-testid="next-month-btn"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-1">
                  {paddingDays.map((_, idx) => (
                    <div key={`pad-${idx}`} className="aspect-square" />
                  ))}
                  {daysInMonth.map(day => {
                    const { events: dayEvents, bookings: dayBookings } = getEventsForDate(day);
                    const hasEvents = dayEvents.length > 0 || dayBookings.length > 0;
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        className={`
                          aspect-square p-1 rounded-xl transition-all relative
                          ${isToday(day) ? 'ring-2 ring-primary' : ''}
                          ${isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/50'}
                          ${!isSameMonth(day, currentMonth) ? 'opacity-30' : ''}
                        `}
                        data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <span className="text-sm font-medium">{format(day, 'd')}</span>
                        {hasEvents && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {dayBookings.length > 0 && (
                              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                            )}
                            {dayEvents.length > 0 && (
                              <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <span className="text-muted-foreground">Bookings</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-purple-400" />
                    <span className="text-muted-foreground">Personal Events</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Selected Date Details */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                  {selectedDate 
                    ? format(selectedDate, 'MMMM d, yyyy')
                    : 'Select a Date'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedDate ? (
                  <>
                    {/* Bookings for selected date */}
                    {(() => {
                      const { events: dayEvents, bookings: dayBookings } = getEventsForDate(selectedDate);
                      
                      if (dayBookings.length === 0 && dayEvents.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <p className="text-muted-foreground text-sm">No events on this day</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-4 rounded-full"
                              onClick={() => setShowAddEvent(true)}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Event
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <>
                          {dayBookings.map(booking => (
                            <div
                              key={booking.event_id}
                              className="p-4 rounded-xl bg-green-500/10 border border-green-500/30"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <Briefcase className="h-4 w-4 text-green-400" />
                                <span className="font-medium text-green-400">Booking</span>
                              </div>
                              <p className="font-medium">{booking.title}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <Clock className="h-3 w-3" />
                                {format(new Date(booking.start_time), 'h:mm a')} - {format(new Date(booking.end_time), 'h:mm a')}
                              </p>
                            </div>
                          ))}

                          {dayEvents.map(event => (
                            <div
                              key={event.event_id}
                              className={`p-4 rounded-xl ${getEventTypeColor(event.event_type)}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <Badge variant="outline" className="text-xs">
                                  {event.event_type}
                                </Badge>
                                <button
                                  onClick={() => toggleEventVisibility(event.event_id, event.is_public)}
                                  className="p-1 hover:bg-white/10 rounded"
                                  title={event.is_public ? 'Make Private' : 'Make Public'}
                                >
                                  {event.is_public ? (
                                    <Eye className="h-4 w-4 text-green-400" />
                                  ) : (
                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </button>
                              </div>
                              <p className="font-medium">{event.title}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <Clock className="h-3 w-3" />
                                {format(new Date(event.start_time), 'h:mm a')} - {format(new Date(event.end_time), 'h:mm a')}
                              </p>
                              {event.location && (
                                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                  <MapPin className="h-3 w-3" />
                                  {event.location}
                                </p>
                              )}
                              {event.description && (
                                <p className="text-sm text-muted-foreground mt-2">
                                  {event.description}
                                </p>
                              )}
                            </div>
                          ))}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Click on a date to see events
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">This Month</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Bookings</span>
                  <span className="font-bold text-green-400">{bookings.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Personal Events</span>
                  <span className="font-bold text-purple-400">{events.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Public Events</span>
                  <span className="font-bold text-blue-400">{events.filter(e => e.is_public).length}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
